<!-- Breadcrumb solo en modo página -->
@if (!drawerMode) {
<app-page-breadcrumb [pageTitle]="isEditing ? 'Editar Empresa' : 'Nueva Empresa'"></app-page-breadcrumb>
}

<!-- Modo drawer: form directo | Modo página: con card wrapper -->
@if (drawerMode) {
<div class="p-6">
  <ng-container *ngTemplateOutlet="formContent" />
</div>
} @else {
<div class="grid grid-cols-1 gap-6">
  <app-component-card [title]="isEditing ? 'Editar Empresa' : 'Nueva Empresa'" desc="Complete los datos de la empresa">
    <ng-container *ngTemplateOutlet="formContent" />
  </app-component-card>
</div>
}

<ng-template #formContent>
    <!-- Loading -->
    @if (loading) {
      <div class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando datos...</span>
      </div>
    }

    <!-- Error -->
    @if (error) {
      <div class="mb-4 p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
        <div class="flex items-center gap-3">
          <svg class="w-5 h-5 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p class="text-error-600 dark:text-error-400">{{ error }}</p>
        </div>
      </div>
    }

    <!-- Formulario -->
    @if (!loading) {
      <form [formGroup]="form" (ngSubmit)="guardar()" class="space-y-5">
        <!-- Responsable — editable solo con permiso de edición sobre empresas (inc35).
             QA lo llama "Propietario", pero la etiqueta visible es "Responsable" para
             unificar con Contactos y con la cabecera del detalle de Empresas. -->
        <div>
          <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Responsable
          </label>
          @if (puedeEditarResponsable) {
            <app-entity-select
              [value]="form.value.userPropietyId"
              (valueChange)="onOwnerValueChange($event)"
              (selected)="onOwnerSelected($event)"
              [displayText]="ownerDisplayName"
              [searchFn]="userSearch"
              [labelFn]="userLabel"
              [sublabelFn]="userSublabel"
              [allowClear]="false"
              placeholder="Buscar usuario por nombre o correo…" />
          } @else {
            <div class="h-11 w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-4 py-2.5 text-sm text-gray-800 dark:text-white/90 flex items-center">
              {{ ownerDisplayName || 'Sin asignar' }}
            </div>
          }
        </div>

        <!-- Nombre Comercial y Razón Social -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Nombre Comercial <span class="text-error-500">*</span>
            </label>
            <input
              type="text"
              formControlName="commercialName"
              placeholder="Nombre comercial"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              [class.border-error-500]="hasError('commercialName')"
              [class.border-gray-300]="!hasError('commercialName')"
            />
            @if (hasError('commercialName')) {
              <p class="mt-1.5 text-sm text-error-500">{{ getError('commercialName') }}</p>
            }
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Razón Social <span class="text-error-500">*</span>
            </label>
            <input
              type="text"
              formControlName="businessName"
              placeholder="Razón social o nombre legal"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              [class.border-error-500]="hasError('businessName')"
              [class.border-gray-300]="!hasError('businessName')"
            />
            @if (hasError('businessName')) {
              <p class="mt-1.5 text-sm text-error-500">{{ getError('businessName') }}</p>
            }
          </div>
        </div>

        <!-- Descripción (inc33: límite visible + aviso al superarlo) -->
        <div>
          <div class="mb-1.5 flex items-center justify-between gap-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Descripción
            </label>
            <span
              class="text-xs tabular-nums"
              [class.text-error-500]="excedeLimite('description', MAX_DESCRIPTION)"
              [class.font-medium]="excedeLimite('description', MAX_DESCRIPTION)"
              [class.text-gray-500]="!excedeLimite('description', MAX_DESCRIPTION)"
              [class.dark:text-gray-400]="!excedeLimite('description', MAX_DESCRIPTION)"
            >
              {{ charCount('description') }} / {{ MAX_DESCRIPTION }}
            </span>
          </div>
          <textarea
            formControlName="description"
            placeholder="Descripción de la empresa"
            rows="3"
            class="dark:bg-dark-900 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            [class.border-error-500]="excedeLimite('description', MAX_DESCRIPTION)"
            [class.border-gray-300]="!excedeLimite('description', MAX_DESCRIPTION)"
          ></textarea>
          @if (excedeLimite('description', MAX_DESCRIPTION)) {
            <p class="mt-1.5 text-sm text-error-500">{{ getError('description') }}</p>
          } @else {
            <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              Máximo {{ MAX_DESCRIPTION }} caracteres.
            </p>
          }
        </div>

        <!-- Teléfono y Sitio Web -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Teléfono
            </label>
            <app-phone-input-flag
              formControlName="phone"
              [countries]="phoneCountries"
              placeholder="Número de teléfono" />
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Sitio Web
            </label>
            <input
              type="text"
              formControlName="website"
              placeholder="https://ejemplo.com"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            />
          </div>
        </div>

        <!-- Dirección Fiscal (inc34: límite visible + aviso al superarlo) -->
        <div>
          <div class="mb-1.5 flex items-center justify-between gap-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Dirección Fiscal
            </label>
            <span
              class="text-xs tabular-nums"
              [class.text-error-500]="excedeLimite('fiscalAddress', MAX_FISCAL_ADDRESS)"
              [class.font-medium]="excedeLimite('fiscalAddress', MAX_FISCAL_ADDRESS)"
              [class.text-gray-500]="!excedeLimite('fiscalAddress', MAX_FISCAL_ADDRESS)"
              [class.dark:text-gray-400]="!excedeLimite('fiscalAddress', MAX_FISCAL_ADDRESS)"
            >
              {{ charCount('fiscalAddress') }} / {{ MAX_FISCAL_ADDRESS }}
            </span>
          </div>
          <input
            type="text"
            formControlName="fiscalAddress"
            placeholder="Dirección fiscal completa"
            class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            [class.border-error-500]="excedeLimite('fiscalAddress', MAX_FISCAL_ADDRESS)"
            [class.border-gray-300]="!excedeLimite('fiscalAddress', MAX_FISCAL_ADDRESS)"
          />
          @if (excedeLimite('fiscalAddress', MAX_FISCAL_ADDRESS)) {
            <p class="mt-1.5 text-sm text-error-500">{{ getError('fiscalAddress') }}</p>
          } @else {
            <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              Máximo {{ MAX_FISCAL_ADDRESS }} caracteres.
            </p>
          }
        </div>

        <!-- Tipo de Identificación Fiscal y Código Fiscal -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Tipo de Identificación Fiscal
            </label>
            <select
              formControlName="idCompanyTaxType"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            >
              <option [ngValue]="null">Seleccione un tipo</option>
              @for (taxType of companyTaxTypes; track taxType.id) {
                <option [ngValue]="taxType.id">{{ taxType.name }}</option>
              }
            </select>
            @if (selectedTaxTypeCountry) {
              <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">País: {{ selectedTaxTypeCountry }}</p>
            }
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Código Fiscal
            </label>
            <input
              type="text"
              formControlName="taxCode"
              placeholder="RUC, RFC, EIN, NIT, etc."
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
            />
          </div>
        </div>

        <!-- Catálogos: Tipo de Relación, Tamaño, Sector, Estado, Ciclo -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Tipo de Relación
            </label>
            <select
              formControlName="idCompanyRelationshipType"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            >
              <option [ngValue]="null">Seleccione un tipo</option>
              @for (type of relationshipTypes; track type.id) {
                <option [ngValue]="type.id">{{ type.name }}</option>
              }
            </select>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Tamaño
            </label>
            <select
              formControlName="idCompanySize"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            >
              <option [ngValue]="null">Seleccione un tamaño</option>
              @for (size of companySizes; track size.id) {
                <option [ngValue]="size.id">{{ size.name }}</option>
              }
            </select>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Sector/Industria
            </label>
            <select
              formControlName="idCompanySector"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            >
              <option [ngValue]="null">Seleccione un sector</option>
              @for (sector of companySectors; track sector.id) {
                <option [ngValue]="sector.id">{{ sector.name }}</option>
              }
            </select>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Estado
            </label>
            <select
              formControlName="idCompanyStateLead"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            >
              <option [ngValue]="null">Seleccione un estado</option>
              @for (stateLead of companyStateLeads; track stateLead.id) {
                <option [ngValue]="stateLead.id">{{ stateLead.name }}</option>
              }
            </select>
          </div>

          <div>
            <label class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Ciclo de Empresa
            </label>
            <select
              formControlName="idCompanyCycle"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
            >
              <option [ngValue]="null">Sin ciclo</option>
              @for (cycle of companyCycles; track cycle.id) {
                <option [ngValue]="cycle.id">{{ cycle.name }}</option>
              }
            </select>
          </div>
        </div>

        <!-- ======== Contactos Asociados ======== -->
        <div class="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <p class="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Contactos Asociados</p>
          <!-- Search and Add Section -->
          <div class="space-y-3 mb-4">
            <!-- Contact search input -->
            <div class="relative">
              <label class="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Buscar contacto por nombre o email
              </label>
              <div class="relative">
                <input
                  type="text"
                  [value]="contactSearchTerm"
                  (input)="onContactSearchInput($any($event.target).value)"
                  (keydown.enter)="onContactSearchEnter($event)"
                  (blur)="hideDropdownDelayed()"
                  placeholder="Buscar contacto... (Enter para ver todos)"
                  class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 pr-8"
                />
                @if (searchingContacts) {
                  <div class="absolute right-2.5 top-2.5">
                    <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
                  </div>
                }
              </div>

              <!-- Dropdown results -->
              @if (showContactDropdown && contactSearchResults.length > 0) {
                <div
                  class="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                  (scroll)="onDropdownScroll($event)"
                >
                  @for (contact of contactSearchResults; track contact.id) {
                    <button
                      type="button"
                      (click)="selectContact(contact)"
                      class="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-left transition-colors">
                      <!-- Avatar -->
                      <div class="flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold {{ getContactAvatarColor(contact) }}">
                        {{ getContactInitials(contact) }}
                      </div>
                      <div class="min-w-0 flex-1">
                        <p class="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {{ contact.fullname }}{{ contact.lastname ? ' ' + contact.lastname : '' }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ contact.mail }}</p>
                      </div>
                    </button>
                  }
                  @if (loadingMoreContacts) {
                    <div class="flex justify-center items-center py-2">
                      <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
                      <span class="ml-2 text-xs text-gray-400 dark:text-gray-500">Cargando más...</span>
                    </div>
                  }
                </div>
              }
            </div>

            <!-- Selected contact + extra fields -->
            @if (selectedContactToAdd) {
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label class="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">
                    Cargo / Posición (opcional)
                  </label>
                  <input
                    type="text"
                    [(ngModel)]="newContactPosition"
                    [ngModelOptions]="{standalone: true}"
                    placeholder="Ej: Director, Gerente..."
                    maxlength="100"
                    class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  />
                </div>
                <div class="flex items-end gap-3">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      [(ngModel)]="newContactIsPrimary"
                      [ngModelOptions]="{standalone: true}"
                      class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-brand-500 focus:ring-brand-500"
                    />
                    <span class="text-sm text-gray-700 dark:text-gray-300">Contacto principal</span>
                  </label>
                  <button
                    type="button"
                    (click)="agregarContacto()"
                    class="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-sm font-medium">
                    Agregar
                  </button>
                </div>
              </div>
            }
          </div>
          <!-- List of associated contacts -->
          @if (asociatedContacts.length > 0) {
            <div class="space-y-2">
              @for (entry of asociatedContacts; track $index) {
                <div class="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <!-- Avatar -->
                  <div class="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold {{ getContactAvatarColor(entry.contact) }}">
                    {{ getContactInitials(entry.contact) }}
                  </div>

                  <!-- Contact info -->
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-2 flex-wrap">
                      <span class="text-sm font-medium text-gray-900 dark:text-white">
                        {{ entry.contact.fullname }}{{ entry.contact.lastname ? ' ' + entry.contact.lastname : '' }}
                      </span>
                      @if (entry.isPrimary) {
                        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-300">
                          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                          </svg>
                          Principal
                        </span>
                      }
                    </div>
                    <div class="flex items-center gap-2 mt-0.5 flex-wrap">
                      @if (entry.contact.mail) {
                        <span class="text-xs text-gray-500 dark:text-gray-400">{{ entry.contact.mail }}</span>
                      }
                      @if (entry.position) {
                        <span class="text-xs text-gray-400 dark:text-gray-500">·</span>
                        <span class="text-xs text-gray-500 dark:text-gray-400 italic">{{ entry.position }}</span>
                      }
                    </div>
                  </div>
      
                  <!-- Actions -->
                  <div class="flex items-center gap-1 flex-shrink-0">
                    <!-- Toggle primary -->
                    <button
                      type="button"
                      (click)="togglePrimary($index)"
                      [title]="entry.isPrimary ? 'Quitar como principal' : 'Marcar como principal'"
                      class="p-1.5 rounded-lg transition-colors"
                      [class.text-brand-500]="entry.isPrimary"
                      [class.hover:text-brand-600]="entry.isPrimary"
                      [class.text-gray-400]="!entry.isPrimary"
                      [class.hover:text-gray-600]="!entry.isPrimary"
                      [class.dark:hover:text-gray-300]="!entry.isPrimary">
                      <svg class="w-4 h-4" [attr.fill]="entry.isPrimary ? 'currentColor' : 'none'" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path>
                      </svg>
                    </button>
                    <!-- Remove -->
                    <button
                      type="button"
                      (click)="quitarContacto($index)"
                      title="Quitar contacto"
                      class="px-3 py-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              }
            </div>
          } @else {
            <div class="text-center py-6 text-gray-400 dark:text-gray-500">
              <svg class="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
              <p class="text-sm">Sin contactos asociados. Use el buscador de arriba para agregar.</p>
            </div>
          }
        </div>
        <!-- ======== Fin Contactos Asociados ======== -->

        <!-- ======== Alerta preventiva: posibles empresas duplicadas (no bloquea el guardado) ======== -->
        @if (showDuplicateWarning) {
          <div class="rounded-lg border border-warning-200 bg-warning-50 p-4 dark:border-warning-500/30 dark:bg-warning-500/10">
            <!-- Header del panel -->
            <div class="flex items-start justify-between gap-3">
              <p class="text-sm font-semibold text-warning-700 dark:text-warning-400">
                ⚠ Posibles empresas duplicadas
              </p>
              <button
                type="button"
                (click)="dismissDuplicateWarning()"
                title="Cerrar advertencia"
                class="flex-shrink-0 p-1 rounded-lg text-warning-500 hover:text-warning-700 hover:bg-warning-100 dark:text-warning-400 dark:hover:text-warning-300 dark:hover:bg-warning-500/20 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
            <p class="mt-1 text-xs text-warning-600 dark:text-warning-400/80">
              Encontramos empresas similares ya registradas. Puede revisar antes de guardar — esta advertencia no impide continuar.
            </p>

            <!-- Candidatos -->
            <div class="mt-3 space-y-2">
              @for (cand of visibleDuplicateCandidates; track cand.id) {
                <div class="flex flex-col gap-2 rounded-lg border border-warning-200/70 bg-white p-3 dark:border-warning-500/20 dark:bg-gray-900 sm:flex-row sm:items-start sm:justify-between">
                  <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ cand.commercialName }}</p>
                    @if (cand.businessName) {
                      <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ cand.businessName }}</p>
                    }
                    <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
                      @for (reason of cand.matchReasons; track reason) {
                        <span class="inline-flex items-center rounded-full bg-warning-100 px-2 py-0.5 text-xs font-medium text-warning-700 dark:bg-warning-500/20 dark:text-warning-400">
                          {{ matchReasonLabel(reason) }}
                        </span>
                      }
                      @if (cand.website) {
                        <span class="max-w-full truncate text-xs text-gray-500 dark:text-gray-400">{{ cand.website }}</span>
                      }
                    </div>
                  </div>
                  <button
                    type="button"
                    (click)="verEmpresaDuplicada(cand.id)"
                    class="flex-shrink-0 self-start text-xs font-medium text-brand-500 hover:text-brand-600 hover:underline dark:text-brand-400 dark:hover:text-brand-300">
                    Ver empresa
                  </button>
                </div>
              }
              @if (duplicateExtraCount > 0) {
                <p class="text-xs text-warning-600 dark:text-warning-400/80">y {{ duplicateExtraCount }} más…</p>
              }
            </div>
          </div>
        }
        <!-- ======== Fin alerta de duplicados ======== -->

        <!-- Botones -->
        <div class="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <app-button variant="outline" type="button" (btnClick)="cancelar()">
            Cancelar
          </app-button>
          <app-button type="button" [disabled]="saving" (btnClick)="guardar()">
            @if (saving) {
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            }
            {{ isEditing ? 'Actualizar' : 'Guardar' }}
          </app-button>
        </div>
      </form>
    }
</ng-template>
